import React, { useEffect, useState, Component } from 'react';
import { X, Share2, Copy, CheckCircle, Facebook, Twitter, Mail, MessageCircle, Gift, Users, Link as LinkIcon, ChevronRight, Award, ArrowRight, Sparkles, Smartphone, QrCode, Coins, Diamond, Heart, Crown, Info, AlertCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useRituals } from '../rituals/RitualContext';
interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  ritualId?: string;
}
export const ReferralModal: React.FC<ReferralModalProps> = ({
  isOpen,
  onClose,
  ritualId
}) => {
  const [step, setStep] = useState<'intro' | 'method' | 'link' | 'social' | 'email' | 'qr' | 'success'>('intro');
  const [copied, setCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState<'link' | 'social' | 'email' | 'qr' | null>(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [pendingReferrals, setPendingReferrals] = useState<Array<{
    id: string;
    name: string;
    status: 'pending' | 'joined' | 'premium';
    date: string;
    image?: string;
  }>>([]);
  const [completedReferrals, setCompletedReferrals] = useState<Array<{
    id: string;
    name: string;
    status: 'joined' | 'premium';
    date: string;
    reward: number;
    rewardType: 'tokens' | 'gems';
    image?: string;
  }>>([]);
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [emailList, setEmailList] = useState<Array<{
    name: string;
    email: string;
  }>>([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    updateRitualProgress,
    completeRitual
  } = useRituals();
  // Generate referral code and link on mount
  useEffect(() => {
    if (isOpen) {
      // Generate random referral code
      const randomCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      setReferralCode(randomCode);
      setReferralLink(`https://sinshift.com/join?ref=${randomCode}`);
      // Load any existing referrals from localStorage
      const savedPendingReferrals = localStorage.getItem('pendingReferrals');
      const savedCompletedReferrals = localStorage.getItem('completedReferrals');
      if (savedPendingReferrals) {
        setPendingReferrals(JSON.parse(savedPendingReferrals));
      }
      if (savedCompletedReferrals) {
        setCompletedReferrals(JSON.parse(savedCompletedReferrals));
      }
      // Set referral count based on completed referrals
      if (savedCompletedReferrals) {
        setReferralCount(JSON.parse(savedCompletedReferrals).length);
      }
    }
  }, [isOpen]);
  // Listen for the openReferralModal event
  useEffect(() => {
    const handleOpenReferralModal = (event: CustomEvent) => {
      if (event.detail && event.detail.ritualId) {
        // You can use the ritualId if needed
      }
    };
    document.addEventListener('openReferralModal', handleOpenReferralModal as EventListener);
    return () => {
      document.removeEventListener('openReferralModal', handleOpenReferralModal as EventListener);
    };
  }, []);
  // Save referrals to localStorage when they change
  useEffect(() => {
    if (pendingReferrals.length > 0) {
      localStorage.setItem('pendingReferrals', JSON.stringify(pendingReferrals));
    }
    if (completedReferrals.length > 0) {
      localStorage.setItem('completedReferrals', JSON.stringify(completedReferrals));
    }
  }, [pendingReferrals, completedReferrals]);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied to clipboard!', {
      description: 'Share this link with your friends to earn rewards.'
    });
    setTimeout(() => setCopied(false), 3000);
  };
  const handleSocialShare = (platform: 'facebook' | 'twitter' | 'whatsapp') => {
    let shareUrl = '';
    const shareText = 'Join me on SinShift - the ultimate fantasy platform!';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${referralLink}`)}`;
        break;
    }
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      // Add a pending referral
      const newPendingReferral = {
        id: Math.random().toString(36).substring(2, 10),
        name: `Friend via ${platform}`,
        status: 'pending' as const,
        date: new Date().toISOString()
      };
      setPendingReferrals([...pendingReferrals, newPendingReferral]);
      // Update ritual progress
      if (ritualId) {
        updateRitualProgress(ritualId, referralCount + 1);
        // Check if ritual is completed
        if (referralCount + 1 >= 1) {
          completeRitual(ritualId);
        }
      }
      // Move to success step
      setStep('success');
    }
  };
  const handleEmailInvite = () => {
    if (!emailInput.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      // Add to email list
      const newEmailInvite = {
        name: nameInput || 'Friend',
        email: emailInput
      };
      setEmailList([...emailList, newEmailInvite]);
      // Add a pending referral
      const newPendingReferral = {
        id: Math.random().toString(36).substring(2, 10),
        name: nameInput || 'Friend via email',
        status: 'pending' as const,
        date: new Date().toISOString()
      };
      setPendingReferrals([...pendingReferrals, newPendingReferral]);
      // Update ritual progress
      if (ritualId) {
        updateRitualProgress(ritualId, referralCount + 1);
        // Check if ritual is completed
        if (referralCount + 1 >= 1) {
          completeRitual(ritualId);
        }
      }
      // Reset form
      setEmailInput('');
      setNameInput('');
      setIsSubmitting(false);
      // Show success message
      toast.success('Invitation sent!', {
        description: `An invitation has been sent to ${newEmailInvite.email}.`
      });
      // Move to success step
      setStep('success');
    }, 1500);
  };
  const simulateReferralAccepted = () => {
    if (pendingReferrals.length === 0) return;
    // Move a pending referral to completed
    const referralToComplete = pendingReferrals[0];
    const newCompletedReferral = {
      id: referralToComplete.id,
      name: referralToComplete.name,
      status: 'joined' as const,
      date: new Date().toISOString(),
      reward: 50,
      rewardType: 'tokens' as const,
      image: referralToComplete.image
    };
    setCompletedReferrals([...completedReferrals, newCompletedReferral]);
    setPendingReferrals(pendingReferrals.slice(1));
    setReferralCount(referralCount + 1);
    // Update ritual progress
    if (ritualId) {
      updateRitualProgress(ritualId, referralCount + 1);
      // Check if ritual is completed
      if (referralCount + 1 >= 1) {
        completeRitual(ritualId);
      }
    }
    toast.success('Referral successful!', {
      description: `${newCompletedReferral.name} has joined SinShift! You earned 50 LuvTokens.`
    });
  };
  const renderStepContent = () => {
    switch (step) {
      case 'intro':
        return <div className="p-6" data-id="element-16367">
            <div className="mb-6 text-center" data-id="element-16368">
              <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-16369">
                <UserPlus size={32} className="text-white" data-id="element-16370" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-16371">
                Invite Friends & Earn Rewards
              </h2>
              <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto" data-id="element-16372">
                Share SinShift with your friends and earn exclusive rewards for
                each friend who joins!
              </p>
            </div>
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl p-4 mb-6" data-id="element-16373">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2" data-id="element-16374">
                <Gift size={18} className="text-pink-500" data-id="element-16375" />
                Referral Rewards
              </h3>
              <ul className="space-y-3" data-id="element-16376">
                <li className="flex items-center gap-3" data-id="element-16377">
                  <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500" data-id="element-16378">
                    <Users size={16} data-id="element-16379" />
                  </div>
                  <div className="flex-1" data-id="element-16380">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-16381">
                      For each friend who joins
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16382">
                      You receive 50 LuvTokens
                    </p>
                  </div>
                </li>
                <li className="flex items-center gap-3" data-id="element-16383">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-500" data-id="element-16384">
                    <Crown size={16} data-id="element-16385" />
                  </div>
                  <div className="flex-1" data-id="element-16386">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-16387">
                      For each friend who upgrades to Premium
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16388">
                      You receive 25 DesireGems + 100 LuvTokens
                    </p>
                  </div>
                </li>
                <li className="flex items-center gap-3" data-id="element-16389">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500" data-id="element-16390">
                    <Award size={16} data-id="element-16391" />
                  </div>
                  <div className="flex-1" data-id="element-16392">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-16393">
                      Refer 5+ friends
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16394">
                      Unlock exclusive Angel Skin + 250 LuvTokens
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 mb-6" data-id="element-16395">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-16396">
                <Info size={18} className="text-blue-500" data-id="element-16397" />
                How It Works
              </h3>
              <div className="space-y-4" data-id="element-16398">
                <div className="flex items-start gap-3" data-id="element-16399">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0" data-id="element-16400">
                    1
                  </div>
                  <div data-id="element-16401">
                    <p className="text-sm text-gray-800 dark:text-gray-200" data-id="element-16402">
                      Choose how you want to invite friends
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-16403">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0" data-id="element-16404">
                    2
                  </div>
                  <div data-id="element-16405">
                    <p className="text-sm text-gray-800 dark:text-gray-200" data-id="element-16406">
                      Share your unique referral link via social media, email,
                      or direct message
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-16407">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0" data-id="element-16408">
                    3
                  </div>
                  <div data-id="element-16409">
                    <p className="text-sm text-gray-800 dark:text-gray-200" data-id="element-16410">
                      Get rewarded when your friends join SinShift using your
                      link
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-16411">
              <button onClick={() => setStep('method')} className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 flex items-center gap-2" data-id="element-16412">
                Start Inviting Friends
                <ArrowRight size={18} data-id="element-16413" />
              </button>
            </div>
          </div>;
      case 'method':
        return <div className="p-6" data-id="element-16414">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center" data-id="element-16415">
              How would you like to invite friends?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-16416">
              <button onClick={() => {
              setShareMethod('link');
              setStep('link');
            }} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex flex-col items-center gap-3" data-id="element-16417">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500" data-id="element-16418">
                  <LinkIcon size={24} data-id="element-16419" />
                </div>
                <div className="text-center" data-id="element-16420">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16421">
                    Share Link
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16422">
                    Copy and share your referral link
                  </p>
                </div>
              </button>
              <button onClick={() => {
              setShareMethod('social');
              setStep('social');
            }} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex flex-col items-center gap-3" data-id="element-16423">
                <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500" data-id="element-16424">
                  <Share2 size={24} data-id="element-16425" />
                </div>
                <div className="text-center" data-id="element-16426">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16427">
                    Social Media
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16428">
                    Share on your favorite platforms
                  </p>
                </div>
              </button>
              <button onClick={() => {
              setShareMethod('email');
              setStep('email');
            }} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex flex-col items-center gap-3" data-id="element-16429">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-500" data-id="element-16430">
                  <Mail size={24} data-id="element-16431" />
                </div>
                <div className="text-center" data-id="element-16432">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16433">
                    Email Invite
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16434">
                    Send personalized email invitations
                  </p>
                </div>
              </button>
              <button onClick={() => {
              setShareMethod('qr');
              setStep('qr');
            }} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex flex-col items-center gap-3" data-id="element-16435">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-500" data-id="element-16436">
                  <QrCode size={24} data-id="element-16437" />
                </div>
                <div className="text-center" data-id="element-16438">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16439">
                    QR Code
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16440">
                    Share in person with a QR code
                  </p>
                </div>
              </button>
            </div>
            <div className="flex items-center justify-between" data-id="element-16441">
              <button onClick={() => setStep('intro')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16442">
                Back
              </button>
              <button onClick={() => setShowRewards(true)} className="px-4 py-2 text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16443">
                View My Referrals ({referralCount})
              </button>
            </div>
          </div>;
      case 'link':
        return <div className="p-6" data-id="element-16444">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center" data-id="element-16445">
              Share Your Referral Link
            </h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6" data-id="element-16446">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-16447">
                Your unique referral link:
              </p>
              <div className="flex items-center gap-2" data-id="element-16448">
                <input type="text" value={referralLink} readOnly className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm" data-id="element-16449" />
                <button onClick={handleCopyLink} className={`p-3 ${copied ? 'bg-green-500' : 'bg-blue-500'} text-white rounded-lg transition-colors duration-300`} data-id="element-16450">
                  {copied ? <CheckCircle size={20} data-id="element-16451" /> : <Copy size={20} data-id="element-16452" />}
                </button>
              </div>
              {copied && <p className="text-xs text-green-500 mt-2" data-id="element-16453">
                  Link copied to clipboard!
                </p>}
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6" data-id="element-16454">
              <div className="flex items-start gap-3" data-id="element-16455">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-500" data-id="element-16456">
                  <Info size={20} data-id="element-16457" />
                </div>
                <div data-id="element-16458">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-16459">
                    How to use your referral link
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16460">
                    Share this link with friends via messaging apps, social
                    media, or anywhere else. When they sign up using your link,
                    you'll both receive rewards!
                  </p>
                </div>
              </div>
            </div>
            <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-4" data-id="element-16461">
              You can also share directly to:
            </p>
            <div className="flex justify-center gap-4 mb-6" data-id="element-16462">
              <button onClick={() => handleSocialShare('facebook')} className="w-12 h-12 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-16463">
                <Facebook size={24} data-id="element-16464" />
              </button>
              <button onClick={() => handleSocialShare('twitter')} className="w-12 h-12 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-16465">
                <Twitter size={24} data-id="element-16466" />
              </button>
              <button onClick={() => handleSocialShare('whatsapp')} className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-16467">
                <MessageCircle size={24} data-id="element-16468" />
              </button>
            </div>
            <div className="flex items-center justify-between" data-id="element-16469">
              <button onClick={() => setStep('method')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16470">
                Back
              </button>
              <button onClick={simulateReferralAccepted} className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 hover:underline" data-id="element-16471">
                (Demo: Simulate Referral)
              </button>
            </div>
          </div>;
      case 'social':
        return <div className="p-6" data-id="element-16472">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center" data-id="element-16473">
              Share on Social Media
            </h2>
            <div className="grid grid-cols-1 gap-4 mb-6" data-id="element-16474">
              <button onClick={() => handleSocialShare('facebook')} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex items-center gap-4" data-id="element-16475">
                <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center text-white" data-id="element-16476">
                  <Facebook size={24} data-id="element-16477" />
                </div>
                <div className="flex-1 text-left" data-id="element-16478">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16479">
                    Share on Facebook
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16480">
                    Share with your Facebook friends
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-400" data-id="element-16481" />
              </button>
              <button onClick={() => handleSocialShare('twitter')} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex items-center gap-4" data-id="element-16482">
                <div className="w-12 h-12 rounded-full bg-[#1DA1F2] flex items-center justify-center text-white" data-id="element-16483">
                  <Twitter size={24} data-id="element-16484" />
                </div>
                <div className="flex-1 text-left" data-id="element-16485">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16486">
                    Share on Twitter
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16487">
                    Tweet your referral link
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-400" data-id="element-16488" />
              </button>
              <button onClick={() => handleSocialShare('whatsapp')} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 flex items-center gap-4" data-id="element-16489">
                <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center text-white" data-id="element-16490">
                  <MessageCircle size={24} data-id="element-16491" />
                </div>
                <div className="flex-1 text-left" data-id="element-16492">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-16493">
                    Share on WhatsApp
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16494">
                    Share with your contacts
                  </p>
                </div>
                <ChevronRight size={20} className="text-gray-400" data-id="element-16495" />
              </button>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6" data-id="element-16496">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-16497">
                Or copy your referral link manually:
              </p>
              <div className="flex items-center gap-2" data-id="element-16498">
                <input type="text" value={referralLink} readOnly className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm" data-id="element-16499" />
                <button onClick={handleCopyLink} className={`p-3 ${copied ? 'bg-green-500' : 'bg-blue-500'} text-white rounded-lg transition-colors duration-300`} data-id="element-16500">
                  {copied ? <CheckCircle size={20} data-id="element-16501" /> : <Copy size={20} data-id="element-16502" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-16503">
              <button onClick={() => setStep('method')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16504">
                Back
              </button>
              <button onClick={simulateReferralAccepted} className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 hover:underline" data-id="element-16505">
                (Demo: Simulate Referral)
              </button>
            </div>
          </div>;
      case 'email':
        return <div className="p-6" data-id="element-16506">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center" data-id="element-16507">
              Invite via Email
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6" data-id="element-16508">
              <div className="mb-4" data-id="element-16509">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-16510">
                  Friend's Name (optional)
                </label>
                <input type="text" value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Enter your friend's name" className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm" data-id="element-16511" />
              </div>
              <div className="mb-4" data-id="element-16512">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-16513">
                  Friend's Email Address
                </label>
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="Enter your friend's email" className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm" data-id="element-16514" />
              </div>
              <button onClick={handleEmailInvite} disabled={isSubmitting || !emailInput.trim()} className={`w-full py-3 ${isSubmitting || !emailInput.trim() ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-teal-500 hover:shadow-md hover:shadow-green-500/20'} text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2`} data-id="element-16515">
                {isSubmitting ? 'Sending...' : 'Send Invitation'}
                {!isSubmitting && <Mail size={18} data-id="element-16516" />}
              </button>
            </div>
            {emailList.length > 0 && <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6" data-id="element-16517">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-16518">
                  Sent Invitations
                </h3>
                <ul className="space-y-2" data-id="element-16519">
                  {emailList.map((invite, index) => <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-16520">
                      <CheckCircle size={16} className="text-green-500" data-id="element-16521" />
                      <span data-id="element-16522">
                        {invite.name} ({invite.email})
                      </span>
                    </li>)}
                </ul>
              </div>}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6" data-id="element-16523">
              <div className="flex items-start gap-3" data-id="element-16524">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-500" data-id="element-16525">
                  <Info size={20} data-id="element-16526" />
                </div>
                <div data-id="element-16527">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-16528">
                    How email invites work
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16529">
                    We'll send a personalized invitation email with your
                    referral link. You'll be notified when they join, and both
                    of you will receive rewards!
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-16530">
              <button onClick={() => setStep('method')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16531">
                Back
              </button>
              <button onClick={simulateReferralAccepted} className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 hover:underline" data-id="element-16532">
                (Demo: Simulate Referral)
              </button>
            </div>
          </div>;
      case 'qr':
        return <div className="p-6" data-id="element-16533">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center" data-id="element-16534">
              Share via QR Code
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6 flex flex-col items-center" data-id="element-16535">
              <div className="w-48 h-48 bg-white p-2 rounded-lg mb-4" data-id="element-16536">
                {/* Placeholder for QR code - in a real app, this would be generated based on the referral link */}
                <div className="w-full h-full bg-[url('https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://sinshift.com/join?ref=DEMO123')] bg-center bg-contain bg-no-repeat" data-id="element-16537"></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4" data-id="element-16538">
                Scan this QR code to join SinShift
              </p>
              <div className="flex gap-3" data-id="element-16539">
                <button onClick={() => {
                // In a real app, this would download the QR code image
                toast.success('QR Code downloaded successfully!', {
                  description: 'You can now share it with your friends.'
                });
              }} className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors" data-id="element-16540">
                  Download QR
                </button>
                <button onClick={handleCopyLink} className={`px-4 py-2 ${copied ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'} rounded-lg text-sm font-medium transition-colors`} data-id="element-16541">
                  {copied ? 'Link Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6" data-id="element-16542">
              <div className="flex items-start gap-3" data-id="element-16543">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-500" data-id="element-16544">
                  <Smartphone size={20} data-id="element-16545" />
                </div>
                <div data-id="element-16546">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-16547">
                    How to use the QR code
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16548">
                    Show this QR code to your friends in person. They can scan
                    it with their phone camera to join SinShift instantly. Great
                    for sharing at gatherings!
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-16549">
              <button onClick={() => setStep('method')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 flex items-center gap-1" data-id="element-16550">
                Back
              </button>
              <button onClick={simulateReferralAccepted} className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 hover:underline" data-id="element-16551">
                (Demo: Simulate Referral)
              </button>
            </div>
          </div>;
      case 'success':
        return <div className="p-6" data-id="element-16552">
            <div className="mb-6 text-center" data-id="element-16553">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-16554">
                <CheckCircle size={32} className="text-white" data-id="element-16555" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-16556">
                Invitation Sent!
              </h2>
              <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto" data-id="element-16557">
                Your invitation has been sent successfully. You'll be notified
                when your friend joins SinShift.
              </p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-xl p-4 mb-6" data-id="element-16558">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2" data-id="element-16559">
                <Gift size={18} className="text-green-500" data-id="element-16560" />
                What happens next?
              </h3>
              <ul className="space-y-3" data-id="element-16561">
                <li className="flex items-start gap-3" data-id="element-16562">
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-500 mt-0.5" data-id="element-16563">
                    <CheckCircle size={14} data-id="element-16564" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16565">
                    Your friend will receive your invitation
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-16566">
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-500 mt-0.5" data-id="element-16567">
                    <CheckCircle size={14} data-id="element-16568" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16569">
                    When they sign up, you'll receive a notification
                  </p>
                </li>
                <li className="flex items-start gap-3" data-id="element-16570">
                  <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-500 mt-0.5" data-id="element-16571">
                    <CheckCircle size={14} data-id="element-16572" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16573">
                    Both you and your friend will receive rewards
                  </p>
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-3" data-id="element-16574">
              <button onClick={() => setStep('method')} className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-16575">
                Invite More Friends
                <UserPlus size={18} data-id="element-16576" />
              </button>
              <button onClick={() => setShowRewards(true)} className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-16577">
                View My Referrals
                <Users size={18} data-id="element-16578" />
              </button>
              <button onClick={onClose} className="w-full py-3 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-700 transition-all duration-300" data-id="element-16579">
                Close
              </button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  const renderRewardsModal = () => {
    if (!showRewards) return null;
    return <div className="absolute inset-0 bg-white dark:bg-gray-900 z-10 overflow-auto" data-id="element-16580">
        <div className="p-6" data-id="element-16581">
          <div className="flex items-center justify-between mb-6" data-id="element-16582">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-16583">
              My Referrals
            </h2>
            <button onClick={() => setShowRewards(false)} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-16584">
              <X size={20} className="text-gray-600 dark:text-gray-400" data-id="element-16585" />
            </button>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 mb-6" data-id="element-16586">
            <div className="flex items-center justify-between" data-id="element-16587">
              <div data-id="element-16588">
                <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-16589">
                  Total Referrals
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16590">
                  Keep inviting to earn more rewards
                </p>
              </div>
              <div className="text-center" data-id="element-16591">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400" data-id="element-16592">
                  {completedReferrals.length}
                </div>
                <p className="text-xs text-purple-500 dark:text-purple-300" data-id="element-16593">
                  Friends Joined
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-16594">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4" data-id="element-16595">
              <div className="flex items-center gap-3 mb-3" data-id="element-16596">
                <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-500" data-id="element-16597">
                  <Heart size={20} data-id="element-16598" />
                </div>
                <div data-id="element-16599">
                  <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-16600">
                    Tokens Earned
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16601">
                    From referrals
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold text-pink-600 dark:text-pink-400" data-id="element-16602">
                {completedReferrals.reduce((total, ref) => {
                return ref.rewardType === 'tokens' ? total + ref.reward : total;
              }, 0)}{' '}
                LuvTokens
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4" data-id="element-16603">
              <div className="flex items-center gap-3 mb-3" data-id="element-16604">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500" data-id="element-16605">
                  <Diamond size={20} data-id="element-16606" />
                </div>
                <div data-id="element-16607">
                  <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-16608">
                    Gems Earned
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-16609">
                    From premium upgrades
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-id="element-16610">
                {completedReferrals.reduce((total, ref) => {
                return ref.rewardType === 'gems' ? total + ref.reward : total;
              }, 0)}{' '}
                DesireGems
              </div>
            </div>
          </div>
          {/* Progress toward special rewards */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6" data-id="element-16611">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3" data-id="element-16612">
              Reward Progress
            </h3>
            <div className="space-y-4" data-id="element-16613">
              <div data-id="element-16614">
                <div className="flex items-center justify-between mb-1" data-id="element-16615">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-16616">
                    Exclusive Angel Skin (5 referrals)
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16617">
                    {completedReferrals.length}/5
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-16618">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500" style={{
                  width: `${completedReferrals.length / 5 * 100}%`
                }} data-id="element-16619"></div>
                </div>
              </div>
              <div data-id="element-16620">
                <div className="flex items-center justify-between mb-1" data-id="element-16621">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-16622">
                    Premium Badge (10 referrals)
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16623">
                    {completedReferrals.length}/10
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-16624">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full transition-all duration-500" style={{
                  width: `${completedReferrals.length / 10 * 100}%`
                }} data-id="element-16625"></div>
                </div>
              </div>
              <div data-id="element-16626">
                <div className="flex items-center justify-between mb-1" data-id="element-16627">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-16628">
                    1 Month Free Premium (25 referrals)
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16629">
                    {completedReferrals.length}/25
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-16630">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500" style={{
                  width: `${completedReferrals.length / 25 * 100}%`
                }} data-id="element-16631"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Pending and completed referrals */}
          <div className="space-y-6" data-id="element-16632">
            {pendingReferrals.length > 0 && <div data-id="element-16633">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3" data-id="element-16634">
                  Pending Invitations ({pendingReferrals.length})
                </h3>
                <div className="space-y-3" data-id="element-16635">
                  {pendingReferrals.map(ref => <div key={ref.id} className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800/30" data-id="element-16636">
                      <div className="flex items-center gap-3" data-id="element-16637">
                        <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center text-yellow-600 dark:text-yellow-400" data-id="element-16638">
                          {ref.image ? <img src={ref.image} alt={ref.name} className="w-full h-full rounded-full object-cover" data-id="element-16639" /> : <Users size={20} data-id="element-16640" />}
                        </div>
                        <div className="flex-1" data-id="element-16641">
                          <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-16642">
                            {ref.name}
                          </h4>
                          <div className="flex items-center gap-2" data-id="element-16643">
                            <span className="text-xs text-yellow-600 dark:text-yellow-400" data-id="element-16644">
                              Pending
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-500" data-id="element-16645">
                              Invited {new Date(ref.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-16646">
                          <button onClick={() => {
                      // Resend invitation
                      toast.success('Invitation resent!', {
                        description: `A reminder has been sent to ${ref.name}.`
                      });
                    }} className="p-2 bg-white dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-16647">
                            <Share2 size={16} data-id="element-16648" />
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
            {completedReferrals.length > 0 && <div data-id="element-16649">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3" data-id="element-16650">
                  Successful Referrals ({completedReferrals.length})
                </h3>
                <div className="space-y-3" data-id="element-16651">
                  {completedReferrals.map(ref => <div key={ref.id} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800/30" data-id="element-16652">
                      <div className="flex items-center gap-3" data-id="element-16653">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center text-green-600 dark:text-green-400" data-id="element-16654">
                          {ref.image ? <img src={ref.image} alt={ref.name} className="w-full h-full rounded-full object-cover" data-id="element-16655" /> : <Users size={20} data-id="element-16656" />}
                        </div>
                        <div className="flex-1" data-id="element-16657">
                          <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-16658">
                            {ref.name}
                          </h4>
                          <div className="flex items-center gap-2" data-id="element-16659">
                            <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/50 rounded-full text-green-600 dark:text-green-400" data-id="element-16660">
                              {ref.status === 'premium' ? 'Premium Member' : 'Joined'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-500" data-id="element-16661">
                              {new Date(ref.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-16662">
                          <div className="font-medium text-gray-900 dark:text-white" data-id="element-16663">
                            +{ref.reward}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-16664">
                            {ref.rewardType === 'tokens' ? 'LuvTokens' : 'Gems'}
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
            {pendingReferrals.length === 0 && completedReferrals.length === 0 && <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center" data-id="element-16665">
                  <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3" data-id="element-16666">
                    <Users size={24} className="text-gray-500 dark:text-gray-400" data-id="element-16667" />
                  </div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-16668">
                    No Referrals Yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-16669">
                    Start inviting friends to earn rewards!
                  </p>
                  <button onClick={() => {
              setShowRewards(false);
              setStep('method');
            }} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300" data-id="element-16670">
                    Invite Friends
                  </button>
                </div>}
          </div>
          <div className="mt-6 flex justify-between" data-id="element-16671">
            <button onClick={() => setShowRewards(false)} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200" data-id="element-16672">
              Back
            </button>
            <button onClick={() => {
            setShowRewards(false);
            setStep('method');
          }} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 flex items-center gap-2" data-id="element-16673">
              Invite More Friends
              <UserPlus size={18} data-id="element-16674" />
            </button>
          </div>
        </div>
      </div>;
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-16675">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-auto animate-in zoom-in-90 duration-300 bg-white dark:bg-gray-900 rounded-xl shadow-xl" data-id="element-16676">
        <button className="absolute right-4 top-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800" onClick={onClose} data-id="element-16677">
          <X size={18} className="text-gray-600 dark:text-gray-400" data-id="element-16678" />
        </button>
        {renderStepContent()}
        {renderRewardsModal()}
      </div>
    </div>;
};